import { useState, useEffect } from "react";
import { Navigation, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react/swiper-react";
import {
  DesktopNav as Desktop,
  MobileNav as Mobile,
  IsDesktopNav,
} from "../../media-queries/MediaQueries";
import AddProduct from "../Global/AddProduct/AddProduct";
import { SIZE } from "../Global/AddProduct/constants";
import "./CollectionCarousel.scss";
import ErrorComponent from "../Common/ErrorComponent/ErrorComponent";
import { HEADING_LEVELS, PRODCOLLECTIONTABID } from "../Global/GlobalConstants";
import { Link } from "react-router-dom";
import { getUrlPath } from "../../helpers/LinkHelper";
import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useProductCache } from "../../hooks/useProductCache";
import Image from "../Global/Image/Image";

import "swiper/swiper.scss";
import "swiper/modules/a11y/a11y.scss";
import "swiper/modules/navigation/navigation.scss";
import Title from "../Global/Title/Title";
import LoadingContainer from "../Common/LoadingContainer/LoadingContainer";
import useLoadingState from "../../hooks/useLoadingState";

const MAXPRODUCTS = 50;
const MOBILEPRODUCTSLIDES = 1;
const TABLETPRODUCTSLIDES = 2;
const DESKTOPPRODUCTSLIDES = 3;

function ProductTile({ product }) {
  const { productCode, childProductName, catalogImage, url } = product;
  const sendDataToGTM = useGTMDispatch();
  const isDesktop = IsDesktopNav();
  const viewDetailsForProduct = ((window.globalProperties && window.globalProperties['viewDetailsForProduct']) || 'View Details for') + ' ' +  productCode;

  const proPlanetSustainableLabel = window.globalProperties?.["proPlanetSustainableLabel"]
    ? window.globalProperties?.["proPlanetSustainableLabel"]
    : "ProPlanet";

  const handleProductClick = () => {
    sendDataToGTM({
      event: "carouselClick",
      productName: childProductName,
      listType: "Collection",
    });
  };

  return (
    <div className="product-collection__carousel__card">
      <div className="product-collection__flag-section">
        {product?.isProPlanet && (
          <p className="pdp-header__active-image-pdp-icon" aria-label={proPlanetSustainableLabel}>
            <img src="https://dart.widen.net/content/vsjmstuwtr/svg/ProPlanet-Leaf-CheckmarkwStroke-Green.svg?u=hsm95h" alt={proPlanetSustainableLabel}/>
          </p>
        )}
      </div>
      <Link
        className="product-collection__carousel__card__link"
        onClick={handleProductClick}
        to={getUrlPath(url)}
      >
        <div className="product-collection__carousel__card__image-wrapper">
          <Image
            className="product-collection__carousel__card__image"
            src={catalogImage}
            loading="lazy"
            alt={viewDetailsForProduct}
          />
          <p className="product-collection__carousel__card__name bold-extra-bold">{childProductName}</p>
        </div>
      </Link>
      <AddProduct
        productCode={productCode}
        productName={childProductName}
        btnSize={isDesktop ? SIZE.LARGE : SIZE.MEDIUM}
        addToListAnalytics={{
          event: "listAdd",
          listType: "Collection",
          productName: `${childProductName}`,
        }}
        removeFromListAnalytics={{
          event: "removeFromList",
          listType: "Collection",
          productName: `${childProductName}`,
        }}
      />
    </div>
  );
}

function ProductCarousel({ products }) {
  if (products.length > MAXPRODUCTS) {
    products = products.slice(0, MAXPRODUCTS);
  }

  return (
    <Swiper
      key={products.length}
      navigation
      modules={[Navigation, A11y]}
      loop={products.length > 1}
      allowSlideNext={false}
      allowSlidePrev={false}
      a11y={{
        prevSlideMessage: (window.globalProperties && window.globalProperties["prevSlideMessage"]) || "Previous slide",
        nextSlideMessage: (window.globalProperties && window.globalProperties["nextSlideMessage"]) || "Next slide"
      }}

      breakpoints={{
        0: {
          slidesPerView: MOBILEPRODUCTSLIDES,
          allowSlideNext: products.length > MOBILEPRODUCTSLIDES ? true : false,
          allowSlidePrev: products.length > MOBILEPRODUCTSLIDES ? true : false,
          spaceBetween: 10,
          touchRatio: 1,
        },
        640: {
          slidesPerView: TABLETPRODUCTSLIDES,
          allowSlideNext: products.length > TABLETPRODUCTSLIDES ? true : false,
          allowSlidePrev: products.length > TABLETPRODUCTSLIDES ? true : false,
          spaceBetween: 10,
          touchRatio: 1,
        },
        992: {
          slidesPerView: DESKTOPPRODUCTSLIDES,
          allowSlideNext: products.length > DESKTOPPRODUCTSLIDES ? true : false,
          allowSlidePrev: products.length > DESKTOPPRODUCTSLIDES ? true : false,
          spaceBetween: 10,
          touchRatio: 1,
          a11y: false,
        },
        1150: {
          slidesPerView: DESKTOPPRODUCTSLIDES,
          allowSlideNext: products.length > DESKTOPPRODUCTSLIDES ? true : false,
          allowSlidePrev: products.length > DESKTOPPRODUCTSLIDES ? true : false,
          spaceBetween: 40,
          touchRatio: 0,
          a11y: false,
        },
      }}
    >
      {products.map((product) => {
        return (
          <SwiperSlide key={product.productCode}>
            <ProductTile
              product={product}
              addToListAnalytics={{
                event: "listAdd",
                listType: "Collection",
                productName: `${product.name}`,
              }}
              removeFromListAnalytics={{
                event: "removeFromList",
                listType: "Collection",
                productName: `${product.name}`,
              }}
            />         
          </SwiperSlide>        
        );
      })}
    </Swiper>
  );
}

function ProductCollectionCarousel({ component, page }) {
  const { document: documentRef, carouselProductCodes } = component.getModels();
  const document = page.getContent(documentRef);
  const { productServletURL } = page.getComponent("header").getModels();
  const [carouselProducts, setCarouselProducts] = useState([]);
  const { isLoading, setSuccess } = useLoadingState(true);

  // Fetch collection products with caching
  const { products: productsData = [] } = useProductCache(
    carouselProductCodes || [],
    productServletURL,
    null,
    null,
    !!((carouselProductCodes?.length || 0) && productServletURL)
  );

  useEffect(() => {
    if (productsData && productsData.length > 0) {
      setCarouselProducts(productsData);
      setSuccess();
    } else if (!carouselProductCodes || carouselProductCodes.length === 0) {
      setSuccess();
    }
  }, [productsData, carouselProductCodes, setSuccess]);

  try {
    if (!document) return null;

    const { primaryTitle = "", link = {} } = document.getData();

    let ctaLink = getUrlPath(component.model.models.productCollectionLink);
    if(ctaLink === undefined) ctaLink = window.location.href;
    return (
      <section className="product-collection" id={PRODCOLLECTIONTABID}>
        <div className="product-collection__inner">
          <div className="product-collection__title-wrapper theme-tertiary">
            <Title defaultLevel={HEADING_LEVELS.THREE} className="product-collection__title">
              {primaryTitle}
            </Title>
            {link && link.label && (
              <Desktop>
                <div className="product-collection__cta-wrapper theme-tertiary">
                  <Link to={ctaLink} className="btn btn--tertiary" id={`${PRODCOLLECTIONTABID}_focus`}>
                    {link.label}
                  </Link>
                </div>
              </Desktop>
            )}
          </div>

          <div className="product-collection__carousel">
            <LoadingContainer
              isLoading={isLoading}
              isEmpty={!carouselProducts || carouselProducts.length === 0}
              emptyMessage="No products available in this collection"
              loadingMessage={`Loading ${primaryTitle} collection`}
              showAlert={true}
            >
              <ProductCarousel products={[...carouselProducts]} />
            </LoadingContainer>
          </div>
        </div>
        {link && link.label && (
          <Mobile>
            <div className="product-collection__cta-wrapper theme-tertiary">
              <Link to={ctaLink} className="btn btn--tertiary" id={`${PRODCOLLECTIONTABID}_focus`}>
                {link.label}
              </Link>
            </div>
          </Mobile>
        )}
      </section>
    );
  } catch (e) {
    console.error("ProductCollectionCarousel error", e);
    return <ErrorComponent error={e} componentName={"ProductCollectionCarousel"} />;
  }
}

export default ProductCollectionCarousel;
